/* eslint-disable @typescript-eslint/no-unused-vars */
import type { DMAPIInterface } from './api-switcher'
import { client } from './client'
import { responseDataConverter, apiPhotoToMyPhoto, apiPhotoToDMPhoto, generateUserIconUrl } from './converters'

export const DMAPI: DMAPIInterface = {
  listDMs: (signal?: AbortSignal) => responseDataConverter(
    client.GET('/dm', { signal }),
    (data) => (data.dms || []).map(dm => ({
      user_id: dm.user_id,
      user_metamory_id: dm.user_metamory_id,
      user_name: dm.user_name,
      user_icon: generateUserIconUrl(dm.user_id),
      last_append: new Date(dm.last_appeared_at),
      unread_count: dm.unread_count || 0,
      last_read_at: dm.last_read_at ? new Date(dm.last_read_at) : undefined
    }))
  ),

  getDM: (target_user_id: string, signal?: AbortSignal) => responseDataConverter(
    client.GET('/dm/{TargetUserId}', {
      params: { path: { TargetUserId: target_user_id } },
      signal
    }),
    (dm) => ({
      user_id: dm.user_id,
      user_metamory_id: dm.user_metamory_id,
      user_name: dm.user_name,
      user_icon: generateUserIconUrl(dm.user_id),
      last_append: new Date(dm.last_appeared_at),
      // TODO: API doesn't return photos in getDM response
      photos: []
    })
  ),


  getPhotoFromDM: async (target_user_id, photo_id, signal) => {
    // Since DM photos now use sent_at as the primary key, we need to get it from the photo list first
    const photoResponse = await client.GET('/dm/{TargetUserId}/photos', {
      params: {
        path: { TargetUserId: target_user_id },
        query: { limit: 100 }
      },
      signal
    })

    if (photoResponse.error) {
      return {
        error: photoResponse.error,
        response: photoResponse.response
      }
    }

    // Find the photo to get its sent_at timestamp
    const photo = photoResponse.data?.photos.find((p: { photo_id: string }) => p.photo_id === photo_id)
    if (!photo) {
      return {
        error: { code: 404, message: 'Photo not found in DM' },
        response: new Response()
      }
    }

    // Now get the specific photo using the new URL structure with sent_at in path
    const response = await client.GET('/dm/{TargetUserId}/photos/{SentAt}', {
      params: {
        path: {
          TargetUserId: target_user_id,
          SentAt: photo.added_at
        }
      },
      signal
    })

    return responseDataConverter(
      Promise.resolve(response),
      (photo) => apiPhotoToDMPhoto(photo)
    )
  },

  listAllPhotosDateFromDM: async (target_user_id, desc, signal) => {
    // Need to fetch all photos to get complete date list
    // This is a limitation of the new pagination API
    let allDates: Date[] = []
    let hasMore = true
    let startFrom: string | undefined = undefined

    while (hasMore) {
      const response: any = await client.GET('/dm/{TargetUserId}/photos', {
        params: {
          path: { TargetUserId: target_user_id },
          query: {
            start_from: startFrom,
            direction: 'past', // Always use past to get all photos
            limit: 100
          }
        },
        signal
      })

      if (response.error) {
        return response
      }

      // Extract dates from this batch - now using added_at since that's the DM sent timestamp
      const dates = response.data.photos.map((photo: any) => new Date(photo.added_at))
      allDates = allDates.concat(dates)

      // Check if there are more photos
      hasMore = response.data.has_more

      // Get the last photo's timestamp for next request
      if (hasMore && response.data.photos.length > 0) {
        const lastPhoto: any = response.data.photos[response.data.photos.length - 1]
        startFrom = lastPhoto.added_at
      }
    }

    // Sort dates based on desc parameter
    const sortedDates = allDates.sort((a, b) => desc ? b.getTime() - a.getTime() : a.getTime() - b.getTime())

    return {
      data: sortedDates,
      response: new Response()
    }
  },

  listPhotosFromDM: (target_user_id, desc, offset_date, signal) => responseDataConverter(
    client.GET('/dm/{TargetUserId}/photos', {
      params: {
        path: { TargetUserId: target_user_id },
        query: {
          start_from: offset_date?.toISOString(),
          direction: desc ? 'past' : 'future',
          limit: 50
        }
      },
      signal
    }),
    (data) => {
      // Now we get full Photo objects with DM context-aware added_at timestamps
      return data.photos.map(photo => apiPhotoToDMPhoto(photo))
    }
  ),

  removePhotosFromDM: async (target_user_id, photo_added_at_timestamps) => {
    // photo_added_at_timestamps should now be passed directly as PhotoAddedAt timestamps
    // This is a breaking change - the frontend now needs to pass timestamps, not photo IDs
    const deleteResponse = await client.DELETE('/dm/{TargetUserId}/photos', {
      params: { path: { TargetUserId: target_user_id } },
      body: { photo_ids: photo_added_at_timestamps }
    })

    return deleteResponse.error
      ? { error: deleteResponse.error, response: deleteResponse.response }
      : { response: deleteResponse.response }
  },

  reactionToDMPhoto: async (target_user_id: string, photo_id: string, reaction: string, add: boolean) => {
    // First, get the photo to extract the correct sent timestamp (added_at)
    const photoResponse = await client.GET('/dm/{TargetUserId}/photos', {
      params: {
        path: { TargetUserId: target_user_id },
        query: { limit: 100 }
      }
    })

    if (photoResponse.error) {
      return {
        error: photoResponse.error,
        response: photoResponse.response
      }
    }

    const photo = photoResponse.data?.photos.find((p: { photo_id: string }) => p.photo_id === photo_id)
    if (!photo) {
      return {
        error: { code: 404, message: 'Photo not found in DM' },
        response: new Response()
      }
    }

    // Use the DM sent timestamp (photo.added_at) not the original photo taken time
    const send_at = photo.added_at

    // First, add/remove the reaction
    const reactionResponse = await client.POST('/dm/{TargetUserId}/{SendAt}/reaction', {
      params: {
        path: {
          TargetUserId: target_user_id,
          SendAt: send_at
        }
      },
      body: {
        target_user_id,
        action: add ? 'add' : 'remove',
        reaction
      }
    })

    if (reactionResponse.error) {
      return {
        error: reactionResponse.error,
        response: reactionResponse.response
      }
    }

    // Return the full Photo object with DM context
    return {
      data: {
        ...apiPhotoToDMPhoto(photo),
        favorite: photo.is_favorite || false
      },
      response: reactionResponse.response
    }
  },

  tagsToDMPhoto: (target_user_id: string, photo_id: string, in_user_ids: string[]) =>
    // TODO: No direct API endpoint for tagging in DM context
    responseDataConverter(
      client.POST('/my/{PhotoId}/tag', {
        params: {
          path: { PhotoId: photo_id },
        },
        body: { in_user_ids },
      }),
      (photo) => ({
        ...apiPhotoToDMPhoto(photo),
        favorite: photo.is_favorite || false
      })
    ),

  reportDMPhoto: (target_user_id, photo_id) => {
    console.log('reportDMPhoto (not implemented)', target_user_id, photo_id)
    return Promise.resolve({
      error: {code: 501, message: 'Not Implemented'},
      response: new Response('', {status: 501})
    })
  },

  appendPhotosToDM: (target_user_id: string, photo_ids: string[]) =>
    // This is the same as createDM
    responseDataConverter(
      client.POST('/dm/{TargetUserId}', {
        params: { path: { TargetUserId: target_user_id } },
        body: { photo_ids }
      }),
      () => undefined
    ),

  markDMAsRead: (target_user_id: string) => responseDataConverter(
    client.POST('/dm/{TargetUserId}/read', {
      params: { path: { TargetUserId: target_user_id } },
      body: {}
    }),
    (data) => ({
      unread_count: data.unread_count,
      last_read_at: data.last_read_at ? new Date(data.last_read_at) : undefined
    })
  )
}